/**
 * @page FlexBewerberDetail
 * @route /flex/bewerber/:id
 * @description Flex-Worker Bewerber-Detail mit 70/30 Split: Aktivitäten-Timeline + Details-Sidebar.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import ConfirmDialog from '../components/ConfirmDialog';
import { ChevronLeft, FileText, Download, Send, Mail, User, Briefcase, MessageSquare, ArrowRightLeft, Clock, Phone, Star, TrendingUp, CalendarDays, AlertTriangle, Trash2, Paperclip, ShieldCheck, ShieldX, CheckCircle2, XCircle, StickyNote, Info, Check, Globe } from 'lucide-react';
import { getCountryName } from '../data/countries';

const API = import.meta.env.VITE_API_URL || '';

/** Strip HTML tags + CSS blocks + raw CSS rules from email body for timeline preview */
function stripHtml(html) {
  if (!html) return '';
  return html
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')   // <style> blocks
    .replace(/<[^>]+>/g, ' ')                           // HTML tags
    .replace(/\w+\s*\{[^}]+\}/g, '')                    // raw CSS rules: table{...}td{...}
    .replace(/\s{2,}/g, ' ')
    .trim();
}

const STATUS_OPTIONS = [
  { value: 'beworben', labelKey: 'flex.bewerber.status.beworben' },
  { value: 'in_review', labelKey: 'flex.bewerber.status.in_review' },
  { value: 'eingeladen', labelKey: 'flex.bewerber.status.eingeladen' },
  { value: 'zugesagt', labelKey: 'flex.bewerber.status.zugesagt' },
  { value: 'aktiv', labelKey: 'flex.bewerber.status.aktiv' },
  { value: 'abgelehnt', labelKey: 'flex.bewerber.status.abgelehnt' },
  { value: 'inaktiv', labelKey: 'flex.bewerber.status.inaktiv' },
];

// Normalize status: strip flex_ prefix from pipeline-derived statuses
const normalizeStatus = (s) => s ? s.replace(/^flex_/, '') : s;

// Human-readable status labels for timeline (replace technical keys like "in_review")
const STATUS_LABEL_MAP = {
  beworben: 'Beworben', in_review: 'In Review', eingeladen: 'Eingeladen',
  zugesagt: 'Zugesagt', aktiv: 'Aktiv', abgelehnt: 'Abgelehnt',
  inaktiv: 'Inaktiv', eingegangen: 'Beworben',
};
function formatStatusText(text, t) {
  if (!text) return text;
  const labels = {};
  STATUS_OPTIONS.forEach(s => { labels[s.value] = t(s.labelKey) || STATUS_LABEL_MAP[s.value] || s.value; });
  labels.eingegangen = labels.beworben;
  return text.replace(/\b(beworben|in_review|eingeladen|zugesagt|aktiv|abgelehnt|inaktiv|eingegangen)\b/g, m => labels[m] || m);
}

// Simple checkmark SVG matching RecruitingDetail IconCheck
const IconCheck = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>;

const STATUS_COLORS = {
  beworben:    { color: '#338edc', bg: '#EBF4FD' },
  in_review:   { color: '#F59E0B', bg: '#FEF3C7' },
  eingeladen:  { color: '#22D3EE', bg: '#CFFAFE' },
  zugesagt:    { color: '#22D3EE', bg: '#CFFAFE' },
  aktiv:       { color: '#10B981', bg: '#D1FAE5' },
  abgelehnt:   { color: '#EF4444', bg: '#FEE2E2' },
  inaktiv:     { color: '#9CA3AF', bg: '#F3F4F6' },
};

const ACTIVITY_ICONS = {
  bewerbung:        { icon: User, color: '#338edc', bg: '#EBF4FD' },
  status_aenderung: { icon: ArrowRightLeft, color: '#F59E0B', bg: '#FEF3C7' },
  email:            { icon: Mail, color: '#338edc', bg: '#EBF4FD' },
  email_in:         { icon: Mail, color: '#10B981', bg: '#D1FAE5' },
  email_out:        { icon: Send, color: '#338edc', bg: '#EBF4FD' },
  sms_out:          { icon: Phone, color: '#6366F1', bg: '#E0E7FF' },
  attachment:       { icon: FileText, color: '#F59E0B', bg: '#FEF3C7' },
  system:           { icon: Star, color: '#6B7280', bg: '#F3F4F6' },
  einsatz_anfrage:  { icon: Briefcase, color: '#6366F1', bg: '#E0E7FF' },
  zusage:           { icon: Briefcase, color: '#10B981', bg: '#D1FAE5' },
  erinnerung:       { icon: Clock, color: '#F59E0B', bg: '#FEF3C7' },
  sms_in:           { icon: Phone, color: '#10B981', bg: '#D1FAE5' },
  telefon_verifiziert: { icon: ShieldCheck, color: '#10B981', bg: '#D1FAE5' },
  notiz:            { icon: StickyNote, color: '#F59E0B', bg: '#FEF3C7' },
  bewertung:        { icon: Star, color: '#F59E0B', bg: '#FEF3C7' },
};

function formatDateTime(d) {
  if (!d) return '–';
  return new Date(d).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatDate(d) {
  if (!d) return '–';
  return new Date(d).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function ScoreBadge({ score }) {
  const cls = score >= 90 ? 'bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-300'
    : score >= 70 ? 'bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300'
    : 'bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-300';
  return (
    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${cls}`}>
      {score}
    </div>
  );
}

function timeAgo(d) {
  if (!d) return '';
  const diff = Date.now() - new Date(d).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `vor ${mins}min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `vor ${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `vor ${days}T`;
  const weeks = Math.floor(days / 7);
  return `vor ${weeks}W`;
}

function cleanEmailText(raw) {
  if (!raw) return '';
  let text = raw;
  // Extract subject line if present
  const subjectMatch = text.match(/Betreff:\s*(.+?)(?:\n|$)/);
  const subject = subjectMatch ? subjectMatch[1].trim() : '';
  // Remove header lines (Von:, Betreff:, then the blank line)
  text = text.replace(/^Von:.*\nBetreff:.*\n\n?/s, '').trim();
  // Cut at quote markers
  const cutPatterns = [/\n-{3,}/, /\nVon:/, /\nFrom:/, /\nGesendet:/, /\nAm\s.*schrieb/];
  for (const p of cutPatterns) {
    const m = text.search(p);
    if (m > 0) text = text.substring(0, m).trim();
  }
  // Strip HTML: remove style blocks, then tags, then raw CSS rules
  text = text.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
  text = text.replace(/<[^>]+>/g, '');
  text = text.replace(/\w+\s*\{[^}]+\}/g, '');
  // Collapse excessive blank lines
  text = text.replace(/\n{3,}/g, '\n\n');
  return { subject, body: text.trim() || raw.substring(0, 500) };
}

function parseAttachment(inhalt) {
  // Format: "Anhang: filename (123 KB)\nPfad: bewerber_id/timestamp_filename"
  const nameMatch = inhalt.match(/Anhang:\s*(.+?)\s*\(/);
  const sizeMatch = inhalt.match(/\((\d+\s*KB)\)/);
  const pathMatch = inhalt.match(/Pfad:\s*(.+)/);
  return {
    name: nameMatch ? nameMatch[1] : inhalt,
    size: sizeMatch ? sizeMatch[1] : '',
    path: pathMatch ? pathMatch[1].trim() : '',
  };
}

export default function FlexBewerberDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const bottomRef = useRef(null);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [unterprozess, setUnterprozess] = useState(null);
  const [dokumente, setDokumente] = useState([]);
  const fileInputRef = useRef(null);

  // Editable fields
  const [notizen, setNotizen] = useState('');
  const [notizenDirty, setNotizenDirty] = useState(false);

  // Stammdaten edit
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});

  // Phone verification
  const [sendingCode, setSendingCode] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [verifyCode, setVerifyCode] = useState('');
  const [verifyError, setVerifyError] = useState('');


  // Confirm dialogs
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmEinladen, setConfirmEinladen] = useState(false);
  const [confirmForceVerify, setConfirmForceVerify] = useState(false);

  // Feedback
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackBewertung, setFeedbackBewertung] = useState(0);
  const [feedbackHover, setFeedbackHover] = useState(0);
  const [savingFeedback, setSavingFeedback] = useState(false);

  // Message form (email / sms)
  const [channel, setChannel] = useState('email');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/flex/bewerber/${id}`, { credentials: 'include' });
      if (!res.ok) { navigate('/recruiting/bewerber?typ=flex'); return; }
      const d = await res.json();
      if (d.status) d.status = normalizeStatus(d.status);
      setData(d);
      setDokumente(d.dokumente || []);
      setNotizen(d.notizen || '');
      setNotizenDirty(false);
      setFeedbackText(d.feedback_text || '');
      setFeedbackBewertung(d.feedback_bewertung || 0);
      // Load unterprozess (hiring sub-process)
      try {
        const uRes = await fetch(`${API}/api/recruiting/${id}/unterprozess`, { credentials: 'include' });
        if (uRes.ok) setUnterprozess(await uRes.json());
      } catch { /* */ }
    } catch {
      navigate('/recruiting/bewerber?typ=flex');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/users`, { credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        setUsers(Array.isArray(d) ? d : d.users || []);
      }
    } catch {}
  }, []);

  useEffect(() => { fetchData(); fetchUsers(); }, [fetchData, fetchUsers]);

  // Cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const sendVerifyCode = async () => {
    setSendingCode(true);
    setVerifyError('');
    try {
      const res = await fetch(`${API}/api/flex/bewerber/${id}/verify-phone`, {
        method: 'POST', credentials: 'include',
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        setCooldown(60);
      } else {
        setVerifyError(d.error || 'Fehler');
        if (d.retry_after) setCooldown(d.retry_after);
      }
    } catch { setVerifyError('Netzwerkfehler'); }
    finally { setSendingCode(false); }
  };

  const confirmVerifyCode = async (force = false) => {
    setVerifyError('');
    try {
      const res = await fetch(`${API}/api/flex/bewerber/${id}/verify-phone/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(force ? { force: true } : { code: verifyCode }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        setVerifyCode('');
        await fetchData();
      } else {
        setVerifyError(d.error || 'Falscher Code');
      }
    } catch { setVerifyError('Netzwerkfehler'); }
  };

  const updateField = async (updates) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/flex/bewerber/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updates),
      });
      if (res.ok) {
        await fetchData();
      }
    } catch {} finally {
      setSaving(false);
    }
  };

  const [confirmReject, setConfirmReject] = useState(false);
  const [infoDialog, setInfoDialog] = useState(null); // #960: { title, message } | null

  const handleStatusChange = async (newStatus) => {
    if (newStatus === data.status) return;
    if ((newStatus === 'zugesagt' || newStatus === 'abgelehnt') && data.status === 'eingeladen' && !data.feedback_text) {
      setInfoDialog({
        title: t('flex.bewerber.feedbackRequiredTitle', 'Feedback erforderlich'),
        message: t('flex.bewerber.feedbackRequired') || 'Bitte zuerst Feedback zum Gespräch erfassen.',
      });
      return;
    }
    if (newStatus === 'aktiv' && !data.telefon_verifiziert) {
      setInfoDialog({
        title: t('flex.verify.requiredForActiveTitle', 'Verifizierung erforderlich'),
        message: t('flex.verify.requiredForActive') || 'Handynummer muss vor Aktivierung verifiziert werden.',
      });
      return;
    }
    if (newStatus === 'eingeladen') {
      setConfirmEinladen(true);
      return;
    }
    if (newStatus === 'abgelehnt') {
      setConfirmReject(true);
      return;
    }
    await updateField({ status: newStatus });
  };

  const handleSaveFeedback = async () => {
    if (!feedbackText.trim()) return;
    setSavingFeedback(true);
    try {
      const res = await fetch(`${API}/api/flex/bewerber/${id}/feedback`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ text: feedbackText.trim(), bewertung: feedbackBewertung || undefined }),
      });
      if (res.ok) {
        await fetchData();
      } else {
        const d = await res.json().catch(() => ({}));
        alert(d.error || 'Fehler beim Speichern');
      }
    } catch {} finally { setSavingFeedback(false); }
  };

  const handleSaveNotizen = async () => {
    await updateField({ notizen });
    setNotizenDirty(false);
  };

  const startEdit = () => {
    setEditData({
      vorname: data.vorname || '',
      nachname: data.nachname || '',
      email: data.email || '',
      telefon: data.telefon || '',
      geburtsdatum: data.geburtsdatum ? data.geburtsdatum.substring(0, 10) : '',
    });
    setIsEditing(true);
  };

  const saveEdit = async () => {
    await updateField(editData);
    setIsEditing(false);
  };

  // Document upload + delete
  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('typ', file.name.toLowerCase().includes('lebenslauf') || file.name.toLowerCase().includes('cv') ? 'lebenslauf' : 'sonstiges');
    try {
      await fetch(`${API}/api/recruiting/bewerber/${id}/dokument`, { method: 'POST', body: fd, credentials: 'include' });
      fetchData();
    } catch { /* */ }
    e.target.value = '';
  };

  const deleteDoc = async (docId) => {
    try {
      await fetch(`${API}/api/recruiting/dokumente/${docId}`, { method: 'DELETE', credentials: 'include' });
      setDokumente(prev => prev.filter(d => d.id !== docId));
    } catch { /* */ }
  };

  const handleSend = async () => {
    if (!emailBody.trim()) return;
    if (channel === 'email' && !emailSubject.trim()) return;
    setSendingEmail(true);
    try {
      let url, body;
      if (channel === 'notiz') {
        url = `${API}/api/flex/bewerber/${id}/notiz`;
        body = { text: emailBody };
      } else if (channel === 'email') {
        url = `${API}/api/flex/bewerber/${id}/email`;
        body = { betreff: emailSubject, nachricht: emailBody };
      } else {
        url = `${API}/api/flex/bewerber/${id}/sms`;
        body = { text: emailBody };
      }
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setEmailSubject('');
        setEmailBody('');
        await fetchData();
      } else {
        const d = await res.json().catch(() => ({}));
        alert(d.error || (channel === 'notiz' ? 'Fehler beim Speichern' : 'Fehler beim Senden'));
      }
    } catch {} finally {
      setSendingEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-[#338edc] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) return null;

  const sc = STATUS_COLORS[data.status] || STATUS_COLORS.beworben;
  const activities = [...(data.aktivitaeten || [])].reverse(); // chronological, oldest first

  return (
    <div className="flex flex-col bg-[var(--bg-card,#fff)]">
      {/* Content: 70/30 split */}
      <div className="flex" style={{ minHeight: '600px' }}>

        {/* Left: Activities (70%) */}
        <div className="flex-1 min-w-0 flex flex-col border-r border-[var(--border-default,#E5E7EB)]">

          {/* #962: Einstellungs-Prozess-Stepper entfernt — Onboarding-Status lebt jetzt als Sidebar-Liste rechts */}

          {/* Activities */}
          <div className="px-6 py-4 space-y-4">
            <h3 className="text-xs font-medium text-[var(--text-secondary,#4B5563)] uppercase tracking-wider">
              {t('flex.bewerber.detail.activities') || 'Aktivitäten'}
            </h3>

            {activities.length === 0 ? (
              <p className="text-sm text-[var(--text-tertiary,#6B7280)] py-8 text-center">
                {t('common.noResults') || 'Keine Aktivitäten'}
              </p>
            ) : activities.map(a => {
              const cfg = ACTIVITY_ICONS[a.typ] || ACTIVITY_ICONS.notiz;
              const Icon = cfg.icon;
              const timestamp = (
                <p className="text-xs text-[var(--text-tertiary,#6B7280)] mt-1">
                  {formatDateTime(a.created_at)}
                  {a.erstellt_von_name && <> &middot; {a.erstellt_von_name}</>}
                </p>
              );

              // Type-specific content
              let content;
              if (a.typ === 'email_in') {
                const parsed = cleanEmailText(a.inhalt);
                content = (
                  <>
                    <p className="text-sm font-medium text-[var(--text-primary,#1A1A2E)]">
                      {t('flex.activity.emailReceived') || 'E-Mail erhalten'}
                      {parsed.subject && <span className="font-normal text-[var(--text-secondary,#4B5563)]"> — {parsed.subject}</span>}
                    </p>
                    <div className="mt-2 p-3 rounded-lg bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-default,#E5E7EB)] text-sm text-[var(--text-secondary,#4B5563)] whitespace-pre-line leading-relaxed max-h-[300px] overflow-y-auto">
                      {parsed.body}
                    </div>
                    {timestamp}
                  </>
                );
              } else if (a.typ === 'email_out') {
                const raw = a.inhalt || '';
                const sepIdx = raw.indexOf('\n---\n');
                const subjectLine = raw.substring(0, sepIdx > 0 ? sepIdx : raw.length);
                const subject = subjectLine.startsWith('Betreff:') ? subjectLine.replace('Betreff:', '').trim() : '';
                // #960: Vollen Body anzeigen — Render-Box ist scrollbar (max-h-[300px] overflow-y-auto)
                const bodyPreview = sepIdx > 0 ? stripHtml(raw.substring(sepIdx + 5)) : '';
                // Email status icon
                const es = a.email_status;
                const StatusIcon = es === 'sent' || es === 'gesendet'
                  ? () => <CheckCircle2 size={14} className="text-green-500 inline ml-1.5" title={t('flex.activity.emailDelivered') || 'Zugestellt'} />
                  : es === 'failed' || es === 'fehler'
                  ? () => <span className="inline-flex items-center ml-1.5 cursor-help" title={a.email_fehler || t('flex.activity.emailFailed') || 'Fehler'}>
                      <XCircle size={14} className="text-red-500" />
                    </span>
                  : es === 'queued' || es === 'held' || es === 'pending'
                  ? () => <Clock size={14} className="text-amber-500 inline ml-1.5" title={t('flex.activity.emailQueued') || 'In Warteschlange'} />
                  : () => null;
                content = (
                  <>
                    <p className="text-sm font-medium text-[var(--text-primary,#1A1A2E)]">
                      {t('flex.activity.emailSent') || 'E-Mail gesendet'}
                      <StatusIcon />
                      {subject && <span className="font-normal text-[var(--text-secondary,#4B5563)]"> — {subject}</span>}
                    </p>
                    {bodyPreview && (
                      <div className="mt-2 p-3 rounded-lg bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-default,#E5E7EB)] text-sm text-[var(--text-secondary,#4B5563)] whitespace-pre-line leading-relaxed max-h-[300px] overflow-y-auto">
                        {bodyPreview}
                      </div>
                    )}
                    {timestamp}
                  </>
                );
              } else if (a.typ === 'sms_out') {
                const smsText = (a.inhalt || '').replace(/^SMS an [^:]+:\n?/, '');
                const vs = a.versand_status;
                const SmsStatusIcon = vs === 'sent'
                  ? () => <CheckCircle2 size={14} className="text-green-500 inline ml-1.5" title="Zugestellt" />
                  : vs === 'failed'
                  ? () => <span className="inline-flex items-center ml-1.5 cursor-help" title={a.versand_fehler || 'Fehler'}>
                      <XCircle size={14} className="text-red-500" />
                    </span>
                  : () => null;
                content = (
                  <>
                    <p className="text-sm font-medium text-[var(--text-primary,#1A1A2E)]">
                      {t('flex.activity.smsSent') || 'SMS gesendet'}
                      <SmsStatusIcon />
                    </p>
                    <p className="mt-1 text-sm text-[var(--text-secondary,#4B5563)]">{smsText}</p>
                    {timestamp}
                  </>
                );
              } else if (a.typ === 'attachment') {
                const att = parseAttachment(a.inhalt);
                content = (
                  <>
                    <p className="text-sm font-medium text-[var(--text-primary,#1A1A2E)]">{t('flex.activity.attachmentReceived') || 'Anhang erhalten'}</p>
                    {att.path ? (
                      <a
                        href={`${API}/api/flex/bewerber/${id}/attachment/${encodeURIComponent(att.path)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-default,#E5E7EB)] text-sm text-[#338edc] hover:border-[#338edc] transition-colors"
                      >
                        <Paperclip size={14} />
                        <span className="truncate max-w-[200px]">{att.name}</span>
                        {att.size && <span className="text-xs text-[var(--text-tertiary,#6B7280)]">({att.size})</span>}
                        <Download size={12} className="text-[var(--text-tertiary,#6B7280)]" />
                      </a>
                    ) : (
                      <p className="mt-1 text-sm text-[var(--text-secondary,#4B5563)]">{att.name}</p>
                    )}
                    {timestamp}
                  </>
                );
              } else if (a.typ === 'notiz') {
                content = (
                  <>
                    <p className="text-sm font-medium text-[var(--text-primary,#1A1A2E)]">
                      {t('flex.bewerber.detail.noteBy', { name: a.erstellt_von_name || '?' })}
                    </p>
                    <div className="mt-2 p-3 rounded-lg border-l-[3px] border-l-amber-400 bg-amber-50/30 dark:bg-amber-900/10 text-sm text-[var(--text-secondary,#4B5563)] whitespace-pre-wrap leading-relaxed">
                      {a.inhalt}
                    </div>
                    {timestamp}
                  </>
                );
              } else if (a.typ === 'status_aenderung') {
                content = (
                  <>
                    <p className="text-sm text-[var(--text-primary,#1A1A2E)]">{formatStatusText(a.inhalt, t)}</p>
                    {timestamp}
                  </>
                );
              } else if (a.typ === 'system') {
                content = (
                  <>
                    <p className="text-sm text-[var(--text-secondary,#4B5563)] italic">{a.inhalt}</p>
                    {timestamp}
                  </>
                );
              } else if (a.typ === 'email') {
                // Legacy email type — strip HTML/CSS like email_out
                const raw = a.inhalt || '';
                const sepIdx = raw.indexOf('\n---\n');
                const subjectLine = raw.substring(0, sepIdx > 0 ? sepIdx : raw.length);
                const subject = subjectLine.startsWith('Betreff:') ? subjectLine.replace('Betreff:', '').trim() : '';
                // #960: Vollen Body anzeigen — Render-Box ist scrollbar (max-h-[300px] overflow-y-auto)
                const bodyPreview = sepIdx > 0 ? stripHtml(raw.substring(sepIdx + 5)) : stripHtml(raw);
                content = (
                  <>
                    <p className="text-sm font-medium text-[var(--text-primary,#1A1A2E)]">
                      {t('flex.activity.emailSent') || 'E-Mail gesendet'}
                      {subject && <span className="font-normal text-[var(--text-secondary,#4B5563)]"> — {subject}</span>}
                    </p>
                    {bodyPreview && (
                      <div className="mt-2 p-3 rounded-lg bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-default,#E5E7EB)] text-sm text-[var(--text-secondary,#4B5563)] whitespace-pre-line leading-relaxed max-h-[300px] overflow-y-auto">
                        {bodyPreview}
                      </div>
                    )}
                    {timestamp}
                  </>
                );
              } else {
                // Safety: strip any HTML/CSS in case content contains raw email markup
                content = (
                  <>
                    <p className="text-sm text-[var(--text-primary,#1A1A2E)]">{stripHtml(a.inhalt)}</p>
                    {timestamp}
                  </>
                );
              }

              return (
                <div key={a.id} className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: cfg.bg }}>
                    <Icon size={14} style={{ color: cfg.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    {content}
                  </div>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>

          {/* Message compose (sticky bottom) */}
          <div className="flex-shrink-0 border-t border-[var(--border-default,#E5E7EB)] px-6 py-4 bg-[var(--bg-card,#fff)]">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold text-[var(--text-secondary,#4B5563)] uppercase tracking-wider">
                {channel === 'notiz'
                  ? (t('flex.bewerber.detail.addNote') || 'Notiz hinzufügen')
                  : (t('flex.bewerber.detail.messageToApplicant') || 'Nachricht an Bewerber')}
              </p>
              <div className="flex bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-default,#E5E7EB)]">
                <button
                  className={`px-3 py-1 text-xs rounded-lg transition-colors font-medium ${channel === 'email' ? 'bg-white text-[var(--text-primary,#1A1A2E)] shadow-sm' : 'text-[var(--text-tertiary,#6B7280)]'}`}
                  onClick={() => setChannel('email')}
                >
                  E-Mail
                </button>
                <button
                  className={`px-3 py-1 text-xs rounded-lg transition-colors font-medium ${channel === 'sms' ? 'bg-white text-[var(--text-primary,#1A1A2E)] shadow-sm' : 'text-[var(--text-tertiary,#6B7280)]'}`}
                  onClick={() => setChannel('sms')}
                >
                  SMS
                </button>
                <button
                  className={`px-3 py-1 text-xs rounded-lg transition-colors font-medium ${channel === 'notiz' ? 'bg-white text-[var(--text-primary,#1A1A2E)] shadow-sm' : 'text-[var(--text-tertiary,#6B7280)]'}`}
                  onClick={() => setChannel('notiz')}
                >
                  {t('flex.bewerber.detail.note') || 'Notiz'}
                </button>
              </div>
            </div>

            {channel === 'email' && (
              <>
                <input
                  type="text"
                  value={emailSubject}
                  onChange={e => setEmailSubject(e.target.value)}
                  placeholder={t('common.subject') || 'Betreff'}
                  className="w-full px-3 py-2 mb-2 rounded-lg border border-[var(--border-default,#E5E7EB)] bg-[var(--bg-input,#fff)] text-sm text-[var(--text-primary,#1A1A2E)] placeholder-[var(--text-tertiary,#6B7280)]"
                />
                <textarea
                  value={emailBody}
                  onChange={e => setEmailBody(e.target.value)}
                  placeholder={t('common.message') || 'Nachricht...'}
                  rows={4}
                  className="w-full px-3 py-2 rounded-lg border border-[var(--border-default,#E5E7EB)] bg-[var(--bg-input,#fff)] text-sm text-[var(--text-primary,#1A1A2E)] placeholder-[var(--text-tertiary,#6B7280)] resize-y min-h-[120px] max-h-[200px]"
                />
              </>
            )}

            {channel === 'sms' && (
              <div>
                <textarea
                  value={emailBody}
                  onChange={e => setEmailBody(e.target.value)}
                  placeholder="SMS Text..."
                  rows={3}
                  maxLength={640}
                  className="w-full px-3 py-2 rounded-lg border border-[var(--border-default,#E5E7EB)] bg-[var(--bg-input,#fff)] text-sm text-[var(--text-primary,#1A1A2E)] placeholder-[var(--text-tertiary,#6B7280)] resize-y min-h-[80px] max-h-[160px]"
                />
                <p className="text-xs text-[var(--text-tertiary,#6B7280)] mt-1 text-right">
                  {emailBody.length}/160 {t('flex.bewerber.detail.smsChars') || 'Zeichen'}
                  {emailBody.length > 160 && ` (${Math.ceil(emailBody.length / 160)} SMS)`}
                </p>
              </div>
            )}

            {channel === 'notiz' && (
              <textarea
                value={emailBody}
                onChange={e => setEmailBody(e.target.value)}
                placeholder={t('flex.bewerber.detail.notePlaceholder') || 'Interne Notiz hinzufügen...'}
                rows={4}
                className="w-full px-3 py-2 rounded-lg border border-[var(--border-default,#E5E7EB)] bg-[var(--bg-input,#fff)] text-sm text-[var(--text-primary,#1A1A2E)] placeholder-[var(--text-tertiary,#6B7280)] resize-y min-h-[100px] max-h-[200px]"
              />
            )}

            <div className="flex justify-end mt-2">
              <button
                onClick={handleSend}
                disabled={sendingEmail || !emailBody.trim() || (channel === 'email' && !emailSubject.trim())}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-40 transition-colors ${
                  channel === 'notiz'
                    ? 'bg-[#338edc] text-white hover:bg-[#2b7bc4]'
                    : 'bg-[#338edc] text-white hover:bg-[#2b7bc4]'
                }`}
              >
                {sendingEmail
                  ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  : channel === 'notiz' ? <StickyNote size={14} /> : <Send size={14} />}
                {channel === 'notiz'
                  ? (t('flex.bewerber.detail.saveNote') || 'Notiz speichern')
                  : channel === 'email'
                    ? (t('flex.bewerber.detail.sendEmail') || 'E-Mail senden')
                    : (t('flex.bewerber.detail.sendSms') || 'SMS senden')}
              </button>
            </div>
          </div>
        </div>

        {/* Right: Details sidebar (30%) */}
        <div className="w-[340px] flex-shrink-0 px-5 py-4 space-y-5 bg-[var(--bg-page,#F4F5FA)]">

          {/* Status */}
          <div>
            <label className="text-xs font-medium text-[var(--text-secondary,#4B5563)] uppercase tracking-wider">Status</label>
            <select
              value={data.status}
              onChange={e => handleStatusChange(e.target.value)}
              disabled={saving}
              className="mt-1.5 w-full px-3 py-2 rounded-lg border border-[var(--border-default,#E5E7EB)] bg-[var(--bg-card,#fff)] text-sm font-medium text-[var(--text-primary,#1A1A2E)]"
            >
              {STATUS_OPTIONS.map(s => {
                const disabled = data.status === 'eingeladen' && !data.feedback_text && (s.value === 'zugesagt' || s.value === 'abgelehnt');
                return (
                  <option key={s.value} value={s.value} disabled={disabled}>
                    {t(s.labelKey) || s.value}{disabled ? ` (${t('flex.bewerber.feedbackRequired') || 'Feedback erforderlich'})` : ''}
                  </option>
                );
              })}
            </select>
          </div>

          {/* #962: ONBOARDING-STATUS — sichtbar nur bei status IN ('zugesagt','aktiv') */}
          {['zugesagt', 'aktiv'].includes(data.status) && (
            <FlexOnboardingStatusList bewerber={data} t={t} />
          )}

          {/* Wartet auf Bestätigung banner */}
          {data.status === 'zugesagt' && (
            <div className="flex items-start gap-2.5 p-3 rounded-xl bg-cyan-50 border border-cyan-200 dark:bg-cyan-900/20 dark:border-cyan-800">
              <Info size={14} className="text-cyan-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-cyan-700 dark:text-cyan-300 leading-relaxed">
                {t('flex.bewerber.wartetAufBestaetigung') || 'Wartet auf Bestätigung durch Bewerber. Ein Bestätigungslink wurde per E-Mail versendet.'}
              </p>
            </div>
          )}

          {/* Feedback Card (visible from status eingeladen) */}
          {['eingeladen', 'zugesagt', 'aktiv', 'abgelehnt', 'inaktiv'].includes(data.status) && (
            <div className="rounded-xl border border-[var(--border-default,#E5E7EB)] bg-[var(--bg-card,#fff)] p-4">
              <div className="flex items-center gap-2 mb-3">
                <MessageSquare size={14} className="text-[var(--text-secondary,#4B5563)]" />
                <label className="text-xs font-medium text-[var(--text-secondary,#4B5563)] uppercase tracking-wider flex-1">
                  {t('flex.bewerber.feedback') || 'Gesprächs-Feedback'}
                </label>
                {data.feedback_text && (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300">
                    {t('flex.bewerber.feedbackSaved') || 'Gespeichert'}
                  </span>
                )}
              </div>

              {/* Star rating */}
              <div className="mb-3">
                <p className="text-xs text-[var(--text-tertiary,#6B7280)] mb-1.5">
                  {t('flex.bewerber.feedbackBewertung') || 'Bewertung'}
                </p>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map(s => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setFeedbackBewertung(s)}
                      onMouseEnter={() => setFeedbackHover(s)}
                      onMouseLeave={() => setFeedbackHover(0)}
                      className="p-0.5 transition-transform hover:scale-110"
                    >
                      <Star
                        size={20}
                        fill={(feedbackHover || feedbackBewertung) >= s ? '#F59E0B' : 'none'}
                        className={(feedbackHover || feedbackBewertung) >= s ? 'text-amber-400' : 'text-[var(--text-muted,#9CA3AF)]'}
                      />
                    </button>
                  ))}
                </div>
              </div>

              {/* Text */}
              <textarea
                value={feedbackText}
                onChange={e => setFeedbackText(e.target.value)}
                placeholder={t('flex.bewerber.feedbackPlaceholder') || 'Notizen zum Gespräch...'}
                rows={3}
                className="w-full px-3 py-2 rounded-lg border border-[var(--border-default,#E5E7EB)] bg-[var(--bg-input,#fff)] text-sm text-[var(--text-primary,#1A1A2E)] placeholder-[var(--text-tertiary,#6B7280)] resize-y min-h-[60px] max-h-[150px]"
              />

              <button
                onClick={handleSaveFeedback}
                disabled={savingFeedback || !feedbackText.trim()}
                className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-[#338edc] text-white text-xs font-medium hover:bg-[#2b7bc4] disabled:opacity-40 transition-colors"
              >
                {savingFeedback
                  ? <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  : <CheckCircle2 size={12} />}
                {t('flex.bewerber.feedbackSpeichern') || 'Feedback speichern'}
              </button>

              {data.feedback_am && (
                <p className="text-[10px] text-[var(--text-tertiary,#6B7280)] mt-2">
                  {t('flex.bewerber.feedbackAt') || 'Erfasst am'} {formatDateTime(data.feedback_am)}
                </p>
              )}
            </div>
          )}

          {/* Onboarding-Daten Card */}
          {['zugesagt', 'aktiv'].includes(data.status) && (
            <div className="rounded-xl border border-[var(--border-default,#E5E7EB)] bg-[var(--bg-card,#fff)] p-4">
              <div className="flex items-center gap-2 mb-3">
                <FileText size={14} className="text-[var(--text-secondary,#4B5563)]" />
                <label className="text-xs font-medium text-[var(--text-secondary,#4B5563)] uppercase tracking-wider flex-1">
                  {t('flex.bewerber.onboarding.title') || 'Onboarding-Daten'}
                </label>
                {data.onboarding_completed_at ? (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300">
                    {t('flex.bewerber.onboarding.completed') || 'Ausgefüllt'}
                  </span>
                ) : (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
                    {t('flex.bewerber.onboarding.pending') || 'Ausstehend'}
                  </span>
                )}
              </div>
              {data.onboarding_completed_at ? (
                <div className="space-y-2 text-sm">
                  <Row label="SVNR" value={data.svnr || '–'} />
                  <Row label="IBAN" value={data.iban || '–'} />
                  {/* Address — no truncation, allow word-break */}
                  <div>
                    <span className="text-[var(--text-secondary,#4B5563)] text-sm">{t('flex.bewerber.onboarding.adresse') || 'Adresse'}</span>
                    <p className="text-[var(--text-primary,#1A1A2E)] text-sm break-words mt-0.5">{data.adresse || '–'}</p>
                  </div>
                  {/* #948 — Staatsbürgerschaft + Drittstaat-Badge */}
                  <div className="flex justify-between gap-2 items-center">
                    <span className="text-[var(--text-secondary,#4B5563)] flex-shrink-0 inline-flex items-center gap-1">
                      <Globe size={12} /> {t('flex.bewerber.onboarding.staatsbuergerschaft') || 'Staatsbürgerschaft'}
                    </span>
                    <span className="text-[var(--text-primary,#1A1A2E)] inline-flex items-center gap-1.5">
                      {data.staatsbuergerschaft ? getCountryName(data.staatsbuergerschaft) : '–'}
                      {data.ist_drittstaat && (
                        <span className="px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200">
                          {t('flex.bewerber.onboarding.drittstaat') || 'Drittstaat'}
                        </span>
                      )}
                    </span>
                  </div>
                  {/* Ausweis — download link instead of just checkmark */}
                  <div className="flex justify-between gap-2">
                    <span className="text-[var(--text-secondary,#4B5563)] flex-shrink-0">{t('flex.bewerber.onboarding.ausweis') || 'Ausweis'}</span>
                    {data.ausweis_pfad ? (
                      <a href={`${API}/api/flex/bewerber/${id}/ausweis`} target="_blank" rel="noopener noreferrer"
                        className="text-[#338edc] hover:underline inline-flex items-center gap-1 text-sm">
                        <Download size={12} /> {t('common.download') || 'Download'}
                      </a>
                    ) : <span className="text-[var(--text-primary,#1A1A2E)]">–</span>}
                  </div>
                  {/* #948 — Aufenthaltstitel (nur bei Drittstaat anzeigen) */}
                  {data.ist_drittstaat && (
                    <div className="flex justify-between gap-2">
                      <span className="text-[var(--text-secondary,#4B5563)] flex-shrink-0">{t('flex.bewerber.onboarding.aufenthaltstitel') || 'Aufenthaltstitel'}</span>
                      {data.aufenthaltstitel_pfad ? (
                        <a href={`${API}/api/flex/bewerber/${id}/aufenthaltstitel`} target="_blank" rel="noopener noreferrer"
                          className="text-[#338edc] hover:underline inline-flex items-center gap-1 text-sm">
                          <Download size={12} /> {t('common.download') || 'Download'}
                        </a>
                      ) : (
                        <span className="text-rose-700 inline-flex items-center gap-1 text-sm">
                          <AlertTriangle size={12} /> {t('flex.bewerber.onboarding.aufenthaltstitel_fehlt') || 'fehlt'}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="text-xs text-[var(--text-tertiary,#6B7280)] mt-1">
                    {t('flex.bewerber.onboarding.completedAt') || 'Ausgefüllt am'}: {formatDateTime(data.onboarding_completed_at)}
                  </div>
                </div>
              ) : (
                <p className="text-xs text-[var(--text-tertiary,#6B7280)] leading-relaxed">
                  {t('flex.bewerber.onboarding.pendingText') || 'Link wurde mit der Zusage-E-Mail versendet. Warte auf Daten des Bewerbers.'}
                </p>
              )}
            </div>
          )}

          {/* Dokumente Card */}
          <div className="rounded-xl border border-[var(--border-default,#E5E7EB)] bg-[var(--bg-card,#fff)] p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold" style={{ color: 'var(--text-secondary, #4B5563)' }}>
                {t('recruiting.detail.dokumente', 'Dokumente')} ({dokumente.length + (data.lebenslauf_pfad && !dokumente.some(d => d.typ === 'lebenslauf') ? 1 : 0)})
              </h3>
              <button onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center gap-1.5 text-xs font-medium" style={{ color: '#338edc' }}>
                <Paperclip size={12} /> {t('recruiting.detail.hochladen', 'Hochladen')}
              </button>
              <input ref={fileInputRef} type="file" className="hidden" onChange={handleDocUpload}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" />
            </div>
            {data.lebenslauf_pfad && !dokumente.some(d => d.typ === 'lebenslauf') && (
              <a href={`${API}/api/flex/bewerber/${id}/cv`} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 p-2.5 rounded-lg border mb-2 text-sm"
                style={{ borderColor: 'var(--border-card, #E5E7EB)', color: '#338edc' }}>
                <Download size={14} /> {t('recruiting.lebenslaufDownload', 'Lebenslauf herunterladen')}
              </a>
            )}
            {dokumente.length > 0 ? (
              <div className="space-y-1.5">
                {dokumente.map(d => (
                  <div key={d.id} className="flex items-center gap-3 p-2 rounded-lg border" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
                    <FileText size={14} style={{ color: 'var(--text-muted, #9CA3AF)' }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate" style={{ color: 'var(--text-primary, #1A1A2E)' }}>{d.dateiname}</p>
                      <p className="text-[10px]" style={{ color: 'var(--text-muted, #9CA3AF)' }}>
                        {d.typ} · {d.groesse_bytes ? `${Math.round(d.groesse_bytes / 1024)}KB` : ''} · {timeAgo(d.created_at)}
                      </p>
                    </div>
                    <a href={`${API}/api/recruiting/dokumente/${d.id}/download`} target="_blank" rel="noopener noreferrer"
                      className="flex-shrink-0" style={{ color: '#338edc' }}>
                      <Download size={14} />
                    </a>
                    <button onClick={() => deleteDoc(d.id)} className="flex-shrink-0" style={{ color: '#EF4444' }}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            ) : !data.lebenslauf_pfad && (
              <p className="text-xs text-center py-3" style={{ color: 'var(--text-muted, #9CA3AF)' }}>
                {t('recruiting.detail.keineDokumente', 'Keine Dokumente vorhanden')}
              </p>
            )}
          </div>

          {/* Stammdaten */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-[var(--text-secondary,#4B5563)] uppercase tracking-wider">
                {t('common.masterData')}
              </label>
              {!isEditing ? (
                <button onClick={startEdit} className="text-xs text-[#338edc] hover:underline">
                  {t('common.edit') || 'Bearbeiten'}
                </button>
              ) : (
                <div className="flex gap-2">
                  <button onClick={() => setIsEditing(false)} className="text-xs text-[var(--text-tertiary,#6B7280)] hover:underline">
                    {t('common.cancel') || 'Abbrechen'}
                  </button>
                  <button onClick={saveEdit} disabled={saving} className="text-xs text-[#338edc] hover:underline font-medium">
                    {t('common.save') || 'Speichern'}
                  </button>
                </div>
              )}
            </div>

            {!isEditing ? (
              <div className="space-y-2 text-sm">
                <Row label={t('common.firstName')} value={data.vorname} />
                <Row label={t('common.lastName')} value={data.nachname} />
                <Row label="E-Mail" value={
                  data.email
                    ? <a href={`mailto:${data.email}`} className="text-[#338edc] hover:underline">{data.email}</a>
                    : '–'
                } />
                <Row label={t('common.phone')} value={
                  data.telefon
                    ? <a href={`tel:${data.telefon}`} className="text-[#338edc] hover:underline">{data.telefon}</a>
                    : '–'
                } />
                <Row label={t('common.birthdate')} value={formatDate(data.geburtsdatum)} />
              </div>
            ) : (
              <div className="space-y-2">
                <div>
                  <label className="text-xs text-[var(--text-secondary,#4B5563)]">{t('common.firstName')}</label>
                  <input value={editData.vorname} onChange={e => setEditData(d => ({ ...d, vorname: e.target.value }))}
                    className="w-full px-3 py-1.5 border border-[var(--border-default,#E5E7EB)] rounded-lg text-sm mt-0.5 bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)]" />
                </div>
                <div>
                  <label className="text-xs text-[var(--text-secondary,#4B5563)]">{t('common.lastName')}</label>
                  <input value={editData.nachname} onChange={e => setEditData(d => ({ ...d, nachname: e.target.value }))}
                    className="w-full px-3 py-1.5 border border-[var(--border-default,#E5E7EB)] rounded-lg text-sm mt-0.5 bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)]" />
                </div>
                <div>
                  <label className="text-xs text-[var(--text-secondary,#4B5563)]">E-Mail</label>
                  <input type="email" value={editData.email} onChange={e => setEditData(d => ({ ...d, email: e.target.value }))}
                    className="w-full px-3 py-1.5 border border-[var(--border-default,#E5E7EB)] rounded-lg text-sm mt-0.5 bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)]" />
                </div>
                <div>
                  <label className="text-xs text-[var(--text-secondary,#4B5563)]">{t('common.phone')}</label>
                  <input type="tel" value={editData.telefon} onChange={e => setEditData(d => ({ ...d, telefon: e.target.value }))}
                    className="w-full px-3 py-1.5 border border-[var(--border-default,#E5E7EB)] rounded-lg text-sm mt-0.5 bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)]" />
                </div>
                <div>
                  <label className="text-xs text-[var(--text-secondary,#4B5563)]">{t('common.birthdate')}</label>
                  <input type="date" value={editData.geburtsdatum} onChange={e => setEditData(d => ({ ...d, geburtsdatum: e.target.value }))}
                    className="w-full px-3 py-1.5 border border-[var(--border-default,#E5E7EB)] rounded-lg text-sm mt-0.5 bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)]" />
                </div>
              </div>
            )}
          </div>

          {/* Phone Verification */}
          <div className="rounded-xl border border-[var(--border-default,#E5E7EB)] bg-[var(--bg-card,#fff)] p-4">
            <div className="flex items-center gap-2 mb-3">
              {data.telefon_verifiziert
                ? <ShieldCheck size={14} className="text-green-600" />
                : <ShieldX size={14} className="text-amber-500" />}
              <label className="text-xs font-medium text-[var(--text-secondary,#4B5563)] uppercase tracking-wider">
                {t('flex.verify.title') || 'Telefon-Verifizierung'}
              </label>
              {data.telefon_verifiziert && (
                <span className="ml-auto px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300">
                  {t('flex.verify.verified') || 'Verifiziert'}
                </span>
              )}
            </div>

            {data.telefon_verifiziert ? (
              <p className="text-xs text-[var(--text-tertiary,#6B7280)]">
                {t('flex.verify.verifiedAt') || 'Verifiziert am'} {formatDateTime(data.telefon_verifiziert_am)}
              </p>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  {t('flex.verify.notVerified') || 'Handynummer noch nicht verifiziert'}
                </p>

                {/* Send code button */}
                <button
                  onClick={sendVerifyCode}
                  disabled={sendingCode || cooldown > 0 || !data.telefon}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-[var(--border-default,#E5E7EB)] text-sm font-medium hover:border-[#338edc] hover:text-[#338edc] transition-colors disabled:opacity-40 text-[var(--text-primary,#1A1A2E)]"
                >
                  {sendingCode
                    ? <div className="w-3.5 h-3.5 border-2 border-[#338edc] border-t-transparent rounded-full animate-spin" />
                    : <Phone size={14} />}
                  {cooldown > 0
                    ? `${t('flex.verify.resendIn') || 'Erneut in'} ${cooldown}s`
                    : (t('flex.verify.sendCode') || 'Code per SMS senden')}
                </button>

                {/* Code input + confirm */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    maxLength={4}
                    value={verifyCode}
                    onChange={e => setVerifyCode(e.target.value.replace(/\D/g, '').substring(0, 4))}
                    placeholder="Code"
                    className="flex-1 px-3 py-2 border border-[var(--border-default,#E5E7EB)] rounded-lg text-sm text-center font-mono tracking-widest bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)]"
                  />
                  <button
                    onClick={() => confirmVerifyCode(false)}
                    disabled={verifyCode.length !== 4}
                    className="px-3 py-2 rounded-lg bg-[#338edc] text-white text-sm font-medium hover:bg-[#2b7bc4] disabled:opacity-40 transition-colors"
                  >
                    <CheckCircle2 size={14} />
                  </button>
                </div>

                {verifyError && (
                  <p className="text-xs text-red-500">{verifyError}</p>
                )}

                {/* Force verify (HR override) */}
                <button
                  onClick={() => setConfirmForceVerify(true)}
                  className="w-full text-xs text-[var(--text-tertiary,#6B7280)] hover:text-[var(--text-secondary,#4B5563)] transition-colors py-1"
                >
                  {t('flex.verify.forceVerify') || 'Manuell verifizieren (HR)'}
                </button>
              </div>
            )}
          </div>

          {/* Performance */}
          {data.performance && (
            <div className="rounded-xl border border-[var(--border-default,#E5E7EB)] bg-[var(--bg-card,#fff)] p-4">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp size={14} className="text-[var(--text-secondary,#4B5563)]" />
                <label className="text-xs font-medium text-[var(--text-secondary,#4B5563)] uppercase tracking-wider">
                  {t('flex.bewerber.detail.performance')}
                </label>
              </div>
              <div className="flex items-center gap-3 mb-3">
                <ScoreBadge score={data.performance.score} />
                <div>
                  <p className="text-lg font-bold text-[var(--text-primary,#1A1A2E)]">{data.performance.score}</p>
                  <p className="text-[10px] text-[var(--text-tertiary,#6B7280)] uppercase">{t('flex.bewerber.detail.score')}</p>
                </div>
                <div className="ml-auto text-right">
                  <div className="inline-flex items-center gap-1">
                    {[1,2,3,4,5].map(s => (
                      <Star key={s} size={13} fill={data.bewertung_avg != null && s <= Math.round(data.bewertung_avg) ? '#F59E0B' : 'none'}
                        className={data.bewertung_avg != null && s <= Math.round(data.bewertung_avg) ? 'text-amber-400' : 'text-[var(--text-muted,#9CA3AF)]'} />
                    ))}
                  </div>
                  {data.bewertung_avg != null && data.bewertung_count > 0 ? (
                    <>
                      <p className="text-xs font-semibold text-[var(--text-primary,#1A1A2E)]">{parseFloat(data.bewertung_avg).toFixed(1)}</p>
                      <p className="text-[10px] text-[var(--text-tertiary,#6B7280)]">{data.bewertung_count} {t('flex.rating.ratingCount')}</p>
                    </>
                  ) : (
                    <p className="text-[10px] text-[var(--text-tertiary,#6B7280)]">{t('flex.rating.noRatings') || 'Keine Bewertungen'}</p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="rounded-lg bg-[var(--bg-inset,#F8F9FC)] p-2">
                  <p className="text-[var(--text-tertiary,#6B7280)]">{t('flex.bewerber.detail.assignments')}</p>
                  <p className="font-semibold text-[var(--text-primary,#1A1A2E)]">{data.performance.einsaetze}</p>
                </div>
                <div className="rounded-lg bg-[var(--bg-inset,#F8F9FC)] p-2">
                  <p className="text-[var(--text-tertiary,#6B7280)]">{t('flex.bewerber.detail.confirmRate')}</p>
                  <p className="font-semibold text-[var(--text-primary,#1A1A2E)]">{data.performance.zusage_pct}%</p>
                </div>
                <div className="rounded-lg bg-[var(--bg-inset,#F8F9FC)] p-2">
                  <p className="text-[var(--text-tertiary,#6B7280)]">{t('flex.bewerber.detail.noShows')}</p>
                  <p className={`font-semibold ${data.performance.no_shows > 0 ? 'text-red-600' : 'text-[var(--text-primary,#1A1A2E)]'}`}>{data.performance.no_shows}</p>
                </div>
                <div className="rounded-lg bg-[var(--bg-inset,#F8F9FC)] p-2">
                  <p className="text-[var(--text-tertiary,#6B7280)]">{t('flex.bewerber.detail.hoursWorked')}</p>
                  <p className="font-semibold text-[var(--text-primary,#1A1A2E)]">{data.performance.total_stunden}h</p>
                </div>
              </div>
            </div>
          )}

          {/* Verfügbarkeit — always show for flex workers */}
          {data.kategorie === 'flex' && (
            <div>
              <label className="text-xs font-medium text-[var(--text-secondary,#4B5563)] uppercase tracking-wider">
                {t('flex.bewerber.detail.availability')}
              </label>
              {data.verfuegbarkeit ? (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map(day => {
                    const v = data.verfuegbarkeit;
                    const isObj = typeof v === 'object' && v !== null;
                    const active = isObj
                      ? !!v[day.toLowerCase()]
                      : (v === 'vollzeit' || v === day.toLowerCase() ||
                        (v === 'wochentags' && !['Sa', 'So'].includes(day)) ||
                        (v === 'wochenende' && ['Sa', 'So'].includes(day)));
                    return (
                      <span
                        key={day}
                        className={`px-2 py-1 rounded-lg text-xs font-medium ${
                          active
                            ? 'bg-blue-50 text-[#338edc] dark:bg-blue-900/20 dark:text-blue-300'
                            : 'bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-tertiary,#6B7280)]'
                        }`}
                      >
                        {day}
                      </span>
                    );
                  })}
                  {typeof data.verfuegbarkeit === 'string' && (
                    <span className="text-[10px] text-[var(--text-tertiary,#6B7280)] self-center ml-1 capitalize">{data.verfuegbarkeit}</span>
                  )}
                </div>
              ) : (
                <p className="mt-2 text-xs text-[var(--text-tertiary,#6B7280)]">
                  {t('flex.bewerber.detail.noAvailability') || 'Keine Verfügbarkeit hinterlegt'}
                </p>
              )}
            </div>
          )}

          {/* CV */}
          {data.lebenslauf_pfad && (
            <div>
              <label className="text-xs font-medium text-[var(--text-secondary,#4B5563)] uppercase tracking-wider">
                {t('flex.bewerber.detail.cv')}
              </label>
              <a
                href={`${API}/api/flex/bewerber/${id}/cv`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1.5 flex items-center gap-2 px-3 py-2 rounded-lg border border-[var(--border-default,#E5E7EB)] bg-[var(--bg-card,#fff)] hover:border-[#338edc] transition-colors text-sm text-[var(--text-primary,#1A1A2E)]"
              >
                <FileText size={16} className="text-[#338edc]" />
                <span className="flex-1 truncate">{data.lebenslauf_pfad}</span>
                <Download size={14} className="text-[var(--text-tertiary,#6B7280)]" />
              </a>
            </div>
          )}

          {/* Einsatz-Historie */}
          <div>
            <label className="text-xs font-medium text-[var(--text-secondary,#4B5563)] uppercase tracking-wider">
              {t('flex.bewerber.detail.history')}
            </label>
            {(!data.einsatz_historie || data.einsatz_historie.length === 0) ? (
              <p className="mt-1.5 text-xs text-[var(--text-tertiary,#6B7280)]">{t('flex.bewerber.detail.noHistory')}</p>
            ) : (
              <div className="mt-2 space-y-2">
                {data.einsatz_historie.map(h => (
                  <div
                    key={h.id}
                    onClick={() => navigate(`/flex/einsaetze/${h.id}`)}
                    className="flex items-start gap-2 p-2 rounded-lg border border-[var(--border-default,#E5E7EB)] bg-[var(--bg-card,#fff)] hover:border-[#338edc] cursor-pointer transition-colors"
                  >
                    <CalendarDays size={14} className="text-[var(--text-tertiary,#6B7280)] mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-[var(--text-primary,#1A1A2E)] truncate">{h.titel}</p>
                      <p className="text-[10px] text-[var(--text-tertiary,#6B7280)]">
                        {formatDate(h.datum_von)}
                        {h.no_show && <span className="ml-1 text-red-500 font-medium">No-Show</span>}
                      </p>
                    </div>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                      h.zusage_status === 'zugesagt' ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300' : 'bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary,#4B5563)]'
                    }`}>
                      {h.zusage_status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quelle */}
          <div>
            <label className="text-xs font-medium text-[var(--text-secondary,#4B5563)] uppercase tracking-wider">
              {t('flex.bewerber.detail.source')}
            </label>
            <p className="mt-1 text-sm text-[var(--text-primary,#1A1A2E)] capitalize">{data.quelle || '–'}</p>
          </div>

          {/* HR Manager */}
          <div>
            <label className="text-xs font-medium text-[var(--text-secondary,#4B5563)] uppercase tracking-wider">HR-Manager</label>
            <select
              value={data.hr_manager_id || ''}
              onChange={e => updateField({ hr_manager_id: e.target.value ? parseInt(e.target.value, 10) : null })}
              disabled={saving}
              className="mt-1.5 w-full px-3 py-2 rounded-lg border border-[var(--border-default,#E5E7EB)] bg-[var(--bg-card,#fff)] text-sm text-[var(--text-primary,#1A1A2E)]"
            >
              <option value="">– {t('common.none')} –</option>
              {users.filter(u => ['administrator', 'ops_manager'].includes(u.rolle)).map(u => (
                <option key={u.id} value={u.id}>{u.vorname} {u.nachname}</option>
              ))}
            </select>
          </div>

          {/* Kurznotiz */}
          <div>
            <label className="text-xs font-medium text-[var(--text-secondary,#4B5563)] uppercase tracking-wider">
              {t('flex.bewerber.detail.quickNote') || 'Kurznotiz'}
            </label>
            <textarea
              value={notizen}
              onChange={e => { setNotizen(e.target.value); setNotizenDirty(true); }}
              rows={3}
              className="mt-1.5 w-full px-3 py-2 rounded-lg border border-[var(--border-default,#E5E7EB)] bg-[var(--bg-card,#fff)] text-sm text-[var(--text-primary,#1A1A2E)] resize-y min-h-[60px]"
            />
            <p className="text-[10px] text-[var(--text-muted,#9CA3AF)] mt-1">
              {t('flex.bewerber.detail.quickNoteHint') || 'Für ausführliche Notizen die Timeline nutzen'}
            </p>
            {notizenDirty && (
              <button
                onClick={handleSaveNotizen}
                disabled={saving}
                className="mt-1.5 px-4 py-1.5 rounded-lg bg-[#338edc] text-white text-xs font-medium hover:bg-[#2b7bc4] disabled:opacity-50 transition-colors"
              >
                {t('common.save')}
              </button>
            )}
          </div>

          {/* Delete */}
          {!data.mitarbeiter_id && (
            <div className="pt-4 border-t border-[var(--border-default,#E5E7EB)]">
              <button
                onClick={() => setConfirmDelete(true)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-[10px] border border-red-200 text-red-600 text-sm font-medium hover:bg-red-50 transition-colors"
              >
                <Trash2 size={14} />
                {t('recruiting.bewerber.deleteButton', 'Löschen')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Confirm dialog: Einladen ── */}
      {confirmEinladen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setConfirmEinladen(false)}>
          <div className="bg-[var(--bg-card,#fff)] rounded-xl border border-[var(--border-default,#E5E7EB)] p-6 w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-[var(--text-primary,#1A1A2E)] mb-2">
              {t('flex.bewerber.einladenConfirm.titel') || 'Bewerber einladen?'}
            </h3>
            <p className="text-sm text-[var(--text-secondary,#4B5563)] mb-5">
              {t('flex.bewerber.einladenConfirm.text') || 'Es wird automatisch eine Einladungs-E-Mail mit Calendly-Link versendet.'}
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setConfirmEinladen(false)}
                className="px-4 py-2 rounded-lg border border-[var(--border-default,#E5E7EB)] text-sm font-medium text-[var(--text-primary,#1A1A2E)] hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors">
                {t('common.cancel') || 'Abbrechen'}
              </button>
              <button onClick={async () => { setConfirmEinladen(false); await updateField({ status: 'eingeladen' }); }}
                className="px-4 py-2 rounded-lg bg-[#338edc] text-white text-sm font-medium hover:bg-[#2b7bc4] transition-colors">
                {t('flex.bewerber.einladenConfirm.senden') || 'Einladung senden'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Confirm dialog: Ablehnen ── */}
      {confirmReject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setConfirmReject(false)}>
          <div className="bg-[var(--bg-card,#fff)] rounded-xl border border-[var(--border-default,#E5E7EB)] p-6 w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-[var(--text-primary,#1A1A2E)] mb-2">
              {t('flex.bewerber.rejectConfirm.titel') || 'Bewerber ablehnen?'}
            </h3>
            <p className="text-sm text-[var(--text-secondary,#4B5563)] mb-5">
              {t('flex.bewerber.rejectConfirm.text') || 'Es wird automatisch eine Absage-E-Mail gesendet. Diese Aktion kann nicht rückgängig gemacht werden.'}
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setConfirmReject(false)}
                className="px-4 py-2 rounded-lg border border-[var(--border-default,#E5E7EB)] text-sm font-medium text-[var(--text-primary,#1A1A2E)] hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors">
                {t('common.cancel') || 'Abbrechen'}
              </button>
              <button onClick={async () => { setConfirmReject(false); await updateField({ status: 'abgelehnt' }); }}
                className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors">
                {t('flex.bewerber.rejectConfirm.ablehnen') || 'Ablehnen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Confirm dialog: Löschen ── */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setConfirmDelete(false)}>
          <div className="bg-[var(--bg-card,#fff)] rounded-2xl border border-[var(--border-card,#E5E7EB)] w-full max-w-md mx-4 p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-semibold" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
              {t('recruiting.bewerber.deleteConfirmTitle', 'Bewerber löschen')}
            </h3>
            <p className="text-sm mt-2" style={{ color: 'var(--text-secondary, #4B5563)' }}>
              {t('recruiting.bewerber.deleteConfirmText', '{name} wirklich löschen? Alle Aktivitäten, Dokumente und Interviews werden ebenfalls gelöscht.').replace('{name}', `${data.vorname} ${data.nachname}`)}
            </p>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setConfirmDelete(false)}
                className="px-4 py-2 rounded-[10px] border text-sm font-medium"
                style={{ borderColor: 'var(--border-card, #E5E7EB)', color: 'var(--text-secondary, #4B5563)' }}>
                {t('common.cancel', 'Abbrechen')}
              </button>
              <button onClick={async () => {
                setConfirmDelete(false);
                try {
                  const res = await fetch(`${API}/api/recruiting/bewerber/${id}`, { method: 'DELETE', credentials: 'include' });
                  if (res.ok) navigate('/recruiting/bewerber');
                  else { const d = await res.json().catch(() => ({})); alert(d.error || 'Fehler'); }
                } catch { alert('Netzwerkfehler'); }
              }}
                className="px-4 py-2 rounded-[10px] bg-red-600 hover:bg-red-700 text-white text-sm font-semibold">
                {t('recruiting.bewerber.deleteButton', 'Löschen')}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmForceVerify}
        onConfirm={() => { setConfirmForceVerify(false); confirmVerifyCode(true); }}
        onCancel={() => setConfirmForceVerify(false)}
        title={t('flex.verify.forceVerifyTitle', 'Manuell verifizieren')}
        message={t('flex.verify.forceConfirm') || 'Handynummer ohne Code verifizieren? (HR-Override)'}
        confirmLabel={t('flex.verify.forceVerify', 'Verifizieren')}
        cancelLabel={t('common.cancel', 'Abbrechen')}
        variant="primary"
      />

      {/* #960: Info-Dialog ersetzt window.alert() */}
      <ConfirmDialog
        open={!!infoDialog}
        onConfirm={() => setInfoDialog(null)}
        onCancel={() => setInfoDialog(null)}
        title={infoDialog?.title || ''}
        message={infoDialog?.message || ''}
        confirmLabel={t('common.ok', 'Verstanden')}
        infoOnly
      />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-[var(--text-secondary,#4B5563)] flex-shrink-0">{label}</span>
      <span className="text-[var(--text-primary,#1A1A2E)] text-right truncate">{value}</span>
    </div>
  );
}

// ─── #962: Onboarding-Status-Liste (Sidebar, sichtbar bei status IN ('zugesagt','aktiv')) ───
// Flex-Worker: 2-Punkt-Liste (Stammdaten ausgefüllt + Im Pool seit). Kein Stepper.
function FlexOnboardingStatusList({ bewerber: b, t }) {
  const items = [
    {
      key: 'flex_stammdaten',
      label: t('recruiting.onboarding.stammdatenAusgefuellt', 'Stammdaten ausgefüllt'),
      done: !!b.stammdaten_ausgefuellt || !!b.stammdaten_am,
      ts: b.stammdaten_am,
    },
    {
      key: 'flex_pool',
      label: t('recruiting.onboarding.imPoolSeit', 'Im Pool seit'),
      done: b.status === 'aktiv',
      ts: b.eingestellt_am,
    },
  ];
  const fmtDate = (ts) => ts ? new Date(ts).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' }) : null;

  return (
    <div className="rounded-xl border bg-[var(--bg-card,#fff)] p-4" style={{ borderColor: 'var(--border-default,#E5E7EB)' }}>
      <label className="text-xs font-medium uppercase tracking-wider block mb-3" style={{ color: 'var(--text-secondary, #4B5563)' }}>
        {t('recruiting.onboarding.title', 'Onboarding-Status')}
      </label>
      <div className="space-y-2">
        {items.map(item => (
          <div key={item.key} className="flex items-center justify-between gap-3 text-sm">
            <span style={{ color: 'var(--text-secondary, #4B5563)' }}>{item.label}</span>
            {item.done ? (
              <span className="inline-flex items-center gap-1.5 font-medium" style={{ color: '#059669' }}>
                <IconCheck />
                {fmtDate(item.ts) || ''}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5" style={{ color: 'var(--text-muted, #9CA3AF)' }}>
                <span className="text-xs">⏳</span>
                {t('recruiting.onboarding.ausstehend', 'Ausstehend')}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
